import { defineStore } from 'pinia'
import { computed } from 'vue'
import currencyJs from 'currency.js'
import { PaymentRequestTypeEnum, TimesheetTypeEnum } from '@/components/PaymentRequest/enums'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'
import { useExpensesStore } from '@/components/PaymentRequest/store/useExpensesStore'
import { useTaxStore } from '@/components/PaymentRequest/store/useTaxStore'
import { useTimesheetFormStore } from '@/components/Timesheet/store/useTimesheetFormStore'
import { useTimesheetStore } from '@/components/PaymentRequest/store/useTimesheetStore'
import { RateType } from '@/enums'

export const useCalculationsStore = defineStore('payment-request-calculations', () => {
  const paymentRequestStore = usePaymentRequestStore()
  const timesheetsStore = useTimesheetStore()
  const timesheetFormStore = useTimesheetFormStore()
  const expensesStore = useExpensesStore()
  const taxStore = useTaxStore()

  const worksomeFeePercentage = computed(() =>
    currencyJs(paymentRequestStore.worksomeFee * 100, paymentRequestStore.currencyConfig).format(),
  )

  const billableTotalCents = computed(() =>
    paymentRequestStore.paymentRequestForm.billableTotal.replace(/[,.]/g, ''),
  )

  const totalExpensedCents = computed(() =>
    expensesStore.expenseReports.reduce((accumulator, object) => accumulator + object.total, 0),
  )

  const totalExpensedAmountFormatted = computed(() =>
    currencyJs(totalExpensedCents.value, {
      ...paymentRequestStore.currencyConfig,
      fromCents: true,
    }).format(),
  )

  const baseTimeAmount = computed(() => {
    if (paymentRequestStore.paymentRequestType !== PaymentRequestTypeEnum.TIME_X_RATE) {
      return 0
    }
    if (
      timesheetsStore.worksomeTimesheetId &&
      timesheetsStore.timesheetType.value === TimesheetTypeEnum.TIMESHEET
    ) {
      let time = timesheetFormStore.totalPayableTime
      // Only convert when we are using an hour-based timesheet
      if (!timesheetFormStore.useDayInputs) {
        switch (paymentRequestStore.paymentRequestRate.rateType) {
          case RateType.DAILY:
            time = timesheetFormStore.totalPayableTime / 8
            break
          case RateType.WEEKLY:
            time = timesheetFormStore.totalPayableTime / 40
            break
        }
      }
      return time
    }
    return paymentRequestStore.paymentRequestForm.billableTime
  })

  const totalCharged = computed(() => {
    if (paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.TIME_X_RATE) {
      if (
        timesheetsStore.worksomeTimesheetId &&
        timesheetsStore.timesheetType.value === TimesheetTypeEnum.TIMESHEET
      ) {
        let time = timesheetFormStore.totalPayableTimeMinusOvertime
        // Only convert when we are using an hour-based timesheet
        if (!timesheetFormStore.useDayInputs) {
          switch (paymentRequestStore.paymentRequestRate.rateType) {
            case RateType.DAILY:
              time = timesheetFormStore.totalPayableTimeMinusOvertime / 8
              break
            case RateType.WEEKLY:
              time = timesheetFormStore.totalPayableTimeMinusOvertime / 40
              break
          }
        }
        let regularChargedAmount = currencyJs(
          paymentRequestStore.paymentRequestRate.rate,
          paymentRequestStore.currencyConfig,
        ).multiply(time)
        timesheetFormStore.associatedOvertimes?.forEach((overtime) => {
          regularChargedAmount = regularChargedAmount.add(
            currencyJs(overtime.total, paymentRequestStore.currencyConfig).divide(100),
          )
        })

        return regularChargedAmount.format()
      }

      return currencyJs(
        paymentRequestStore.paymentRequestRate.rate,
        paymentRequestStore.currencyConfig,
      )
        .multiply(paymentRequestStore.paymentRequestForm.billableTime)
        .format()
    }

    return currencyJs(billableTotalCents.value, {
      ...paymentRequestStore.currencyConfig,
      fromCents: true,
    }).format()
  })

  const totalChargedNumber = computed(() => {
    const totalChargedCents = totalCharged.value.replace(/[,.]/g, '')
    return Number(totalChargedCents.slice(0, -2)) + Number(`0.${totalChargedCents.slice(-2)}`)
  })

  const feeTotal = computed(() =>
    currencyJs(totalCharged.value, paymentRequestStore.currencyConfig)
      .multiply(paymentRequestStore.worksomeFee)
      .format(),
  )

  const earningsBeforeTax = computed(() => 
    currencyJs(totalCharged.value, paymentRequestStore.currencyConfig)
      .add(totalExpensedAmountFormatted.value)
      .subtract(feeTotal.value)
      .format()
  )

  const subTotal = computed(() => {
    const amountCharged = currencyJs(totalCharged.value, paymentRequestStore.currencyConfig)
    
    const totalTaxed: currencyJs = taxStore.allTaxLines.reduce(
      (accumulator, currentValue) =>
        currencyJs(accumulator, paymentRequestStore.currencyConfig).add(currentValue.amountCharged),
      currencyJs(0, paymentRequestStore.currencyConfig),
    )

    return amountCharged.add(totalTaxed).format()
  })

  const invoiceTotal = computed(() =>
    currencyJs(subTotal.value, paymentRequestStore.currencyConfig)
      .add(totalExpensedAmountFormatted.value)
      .format(),
  )

  const earningsTotal = computed(() =>
    currencyJs(invoiceTotal.value, paymentRequestStore.currencyConfig)
      .subtract(feeTotal.value)
      .format(),
  )

  return {
    totalExpensedAmountFormatted,
    totalCharged,
    subTotal,
    invoiceTotal,
    worksomeFeePercentage,
    feeTotal,
    earningsTotal,
    totalChargedNumber,
    totalExpensedCents,
    baseTimeAmount,
    earningsBeforeTax,
  }
})
